import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Card,
    CardContent,
    CardActionArea,
    IconButton,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Typography,
    SxProps,
    Theme,
} from '@mui/material';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import useActingRole from '../../hooks/useActingRole';
import useLocales from '../../hooks/useLocales';

// -------------------------- Styles --------------------------
const gridContainer: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 3,
    mb: 3,
    mt: 2,
};

const baseCard: SxProps<Theme> = {
    cursor: 'pointer',
    aspectRatio: '1/1',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

const selectedCard: SxProps<Theme> = {
    border: '2px solid',
    borderColor: 'primary.main',
    backgroundColor: 'action.selected',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
};

const unselectedCard: SxProps<Theme> = {
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    '&:hover': {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
};

const cardActionArea: SxProps<Theme> = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
};

const iconBox: SxProps<Theme> = {
    mb: 2.5,
    width: 56,
    height: 56,
    mx: 'auto',
    transition: 'all 0.3s ease',
    color: '#1976d2',
};

const roleText: SxProps<Theme> = {
    fontSize: '1.1rem',
    letterSpacing: '0.3px',
    fontWeight: 500,
    color: 'text.primary',
};

// -------------------------- Component --------------------------
export default function ActingRoleDialog() {
    const { translate } = useLocales();
    const {
        roles,
        actingRole,
        selectedRole,
        setSelectedRole,
        switchRole,
        dialogOpen,
        closeDialog,
        rememberChoice,
        setRememberChoice,
    } = useActingRole();

    const hasChangedRole = selectedRole !== actingRole && actingRole !== null;

    const ICONS: Record<string, string> = {
        user: 'ic_user',
        client: 'ic_group',
        provider: 'ic_psychology',
        users: 'ic_diversity_3',
    };

    const getIcon = (name: string) => (
        <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
    );

    return (
        <Dialog
            open={dialogOpen}
            onClose={closeDialog}
            maxWidth="xs"
            fullWidth
            data-testid="acting-role-dialog"
        >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 700, pb: 2, pr: 6 }}>
                {translate('dialogs.roleSelect.title')}
                <IconButton
                    aria-label="close"
                    onClick={closeDialog}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'text.secondary',
                    }}
                >
                    <Iconify icon="eva:close-fill" width={24} height={24} />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={gridContainer}>
                    {roles?.map((role) => {
                        const lowerRole = role.toLowerCase();
                        const iconName = ICONS[lowerRole] || ICONS.user;
                        const isSelected = selectedRole === role;

                        const cardSx = {
                            ...baseCard,
                            ...(isSelected ? selectedCard : unselectedCard),
                        } as SxProps<Theme>;

                        return (
                            <Card
                                key={role}
                                sx={cardSx}
                                onClick={() => setSelectedRole(role)}
                                data-testid={`acting-role-option-${role}`}
                            >
                                <CardActionArea sx={cardActionArea}>
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <Box sx={{ ...iconBox }}>{getIcon(iconName)}</Box>
                                        <Box sx={{ ...roleText }}>{role}</Box>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Box>

                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberChoice}
                                onChange={(e) => setRememberChoice(e.target.checked)}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                {translate('dialogs.roleSelect.rememberChoice')}
                            </Typography>
                        }
                    />
                    <Tooltip title={translate('dialogs.roleSelect.switchHint')} arrow>
                        <Box sx={{ display: 'flex', cursor: 'pointer', color: 'text.secondary' }}>
                            <Iconify icon="eva:info-outline" width={18} height={18} />
                        </Box>
                    </Tooltip>
                </Box>

                <Button
                    variant="contained"
                    disabled={!selectedRole}
                    onClick={switchRole}
                    fullWidth
                    data-testid="acting-role-submit"
                    sx={{ py: 1.2, fontWeight: 600 }}
                >
                    {hasChangedRole
                        ? translate('dialogs.roleSelect.switchButton')
                        : translate('dialogs.roleSelect.continueButton')}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
